import React, { useEffect, useState } from 'react';
import { ArrowLeft, Plus, Pencil, Trash2, Save, CheckCircle, XCircle, Lock, Unlock,
  FileText, Calendar, AlertTriangle, Users, Clock, CalendarPlus, Download,
  Filter, Search, Eye, Mail, ChevronRight, Copy, RefreshCw } from 'lucide-react';
import { useApi } from './DashboardLayout';

function formatDate(d) {
  if (!d) return '';
  const s = String(d).substring(0, 10);
  const parts = s.split('-');
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : d;
}

function statusBadge(survey) {
  const today = new Date().toISOString().split('T')[0];
  if (!survey.is_active) return <span className="badge badge-neutral" data-testid="status-inactive">Inaktiv</span>;
  if (!survey.input_open) return <span className="badge badge-warning" data-testid="status-closed">Aktiviert – Eingabe gesperrt</span>;
  if (today > survey.end_date) return <span className="badge badge-neutral" data-testid="status-expired">Zeitraum abgelaufen</span>;
  if (today < survey.start_date) return <span className="badge badge-info" data-testid="status-upcoming">Eingabe offen – beginnt am {formatDate(survey.start_date)}</span>;
  return <span className="badge badge-success" data-testid="status-open">Eingabe offen</span>;
}


// ═══════════════════════════════════════════════════════════════
// SURVEY EDITOR
// ═══════════════════════════════════════════════════════════════
function SurveyEditor({ survey, onSave, onCancel, apiFetch }) {
  const [form, setForm] = useState({
    title: '', year: new Date().getFullYear(), start_date: '', end_date: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (survey) setForm({
      title: survey.title || '', year: survey.year || new Date().getFullYear(),
      start_date: survey.start_date || '', end_date: survey.end_date || '',
    });
  }, [survey]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim() || !form.start_date || !form.end_date) return;
    setSaving(true);
    const url = survey ? `/api/surveys/${survey.id}` : '/api/surveys';
    const method = survey ? 'PUT' : 'POST';
    const r = await apiFetch(url, { method, body: JSON.stringify(form) });
    const d = await r?.json();
    setSaving(false);
    if (d?.status) onSave(d.survey);
  };

  return (
    <div data-testid="survey-editor">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={onCancel} data-testid="survey-editor-back">
          <ArrowLeft size={14} /> Zurück
        </button>
        <h2 style={{ margin: 0, fontSize: 20 }}>{survey ? 'Erhebung bearbeiten' : 'Neue Erhebung'}</h2>
      </div>
      <div className="of-card" style={{ marginBottom: 16 }}>
        <div className="of-section">Erhebungszeitraum</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div><label className="of-label">Titel</label>
            <input className="of-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="z.B. Bestandserhebung 2025" data-testid="survey-title" /></div>
          <div><label className="of-label">Jahr</label>
            <input className="of-input" type="number" value={form.year} onChange={e => set('year', e.target.value)} min="2020" max="2050" data-testid="survey-year" /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label className="of-label">Startdatum</label>
            <input className="of-input" type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} data-testid="survey-start-date" /></div>
          <div><label className="of-label">Enddatum</label>
            <input className="of-input" type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} data-testid="survey-end-date" /></div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.title.trim() || !form.start_date || !form.end_date} style={{ padding: '8px 24px' }} data-testid="survey-save-btn">
          <Save size={14} /> {saving ? 'Speichern...' : survey ? 'Aktualisieren' : 'Erstellen'}
        </button>
        <button className="btn btn-secondary" onClick={onCancel} style={{ padding: '8px 20px' }}>Abbrechen</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CONFIRMATION TEXTS MANAGER
// ═══════════════════════════════════════════════════════════════
function ConfirmationTextsManager({ survey, apiFetch, onUpdate }) {
  const [texts, setTexts] = useState(survey?.confirmation_texts || []);
  const [newText, setNewText] = useState('');
  const [newRequired, setNewRequired] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editRequired, setEditRequired] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setTexts(survey?.confirmation_texts || []); }, [survey]);

  const addText = async () => {
    if (!newText.trim()) return;
    setSaving(true);
    const r = await apiFetch(`/api/surveys/${survey.id}/confirmation-texts`, { method: 'POST', body: JSON.stringify({ text: newText, required: newRequired }) });
    const d = await r?.json();
    if (d?.status) { setNewText(''); setNewRequired(true); onUpdate(); }
    setSaving(false);
  };

  const updateText = async (ctId) => {
    setSaving(true);
    const r = await apiFetch(`/api/surveys/${survey.id}/confirmation-texts/${ctId}`, { method: 'PUT', body: JSON.stringify({ text: editText, required: editRequired }) });
    const d = await r?.json();
    if (d?.status) { setEditId(null); onUpdate(); }
    setSaving(false);
  };

  const deleteText = async (ctId) => {
    if (!window.confirm('Bestimmung wirklich löschen?')) return;
    await apiFetch(`/api/surveys/${survey.id}/confirmation-texts/${ctId}`, { method: 'DELETE' });
    onUpdate();
  };

  return (
    <div className="of-card" data-testid="confirmation-texts-manager">
      <div className="of-section" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FileText size={16} /> Zustimmungspflichtige Bestimmungen</div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px' }}>Anbieter müssen diesen Bestimmungen bei der Abgabe zustimmen.</p>
      {texts.length > 0 && <div style={{ marginBottom: 16 }}>
        {texts.map((t, idx) => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0', borderBottom: idx < texts.length - 1 ? '1px solid var(--border)' : 'none' }} data-testid={`ct-item-${t.id}`}>
            {editId === t.id ? (
              <div style={{ flex: 1 }}>
                <textarea className="of-input" value={editText} onChange={e => setEditText(e.target.value)} rows={2} style={{ resize: 'vertical', marginBottom: 6 }} data-testid={`ct-edit-text-${t.id}`} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}><input type="checkbox" checked={editRequired} onChange={e => setEditRequired(e.target.checked)} /> Pflicht</label>
                  <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => updateText(t.id)} disabled={saving} data-testid={`ct-save-${t.id}`}><Save size={12} /> Speichern</button>
                  <button className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => setEditId(null)}>Abbrechen</button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ flex: 1, fontSize: 13 }}><span>{t.text}</span>{t.required && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--primary)', fontWeight: 600 }}>PFLICHT</span>}</div>
                <button className="btn-icon edit" title="Bearbeiten" onClick={() => { setEditId(t.id); setEditText(t.text); setEditRequired(t.required); }} data-testid={`ct-edit-${t.id}`}><Pencil size={13} /></button>
                <button className="btn-icon" title="Löschen" style={{ color: '#EF4444' }} onClick={() => deleteText(t.id)} data-testid={`ct-delete-${t.id}`}><Trash2 size={13} /></button>
              </>
            )}
          </div>
        ))}
      </div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <textarea className="of-input" value={newText} onChange={e => setNewText(e.target.value)} placeholder="Text der Bestimmung eingeben..." rows={2} style={{ resize: 'vertical' }} data-testid="ct-new-text" />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, marginTop: 4, cursor: 'pointer' }}><input type="checkbox" checked={newRequired} onChange={e => setNewRequired(e.target.checked)} data-testid="ct-new-required" /> Pflichtfeld</label>
        </div>
        <button className="btn btn-primary" style={{ padding: '8px 16px', fontSize: 13, whiteSpace: 'nowrap' }} onClick={addText} disabled={saving || !newText.trim()} data-testid="ct-add-btn"><Plus size={14} /> Hinzufügen</button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// SURVEY DETAIL VIEW (Admin)
// ═══════════════════════════════════════════════════════════════
function SurveyDetail({ survey: initialSurvey, onBack, onEdit, apiFetch }) {
  const [survey, setSurvey] = useState(initialSurvey);
  const [sending, setSending] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [testEmail, setTestEmail] = useState('claudio.gaertner@bgpr.org');
  const [reminderSettings, setReminderSettings] = useState({ enabled: false, days_before_end: 7 });
  const [savingReminder, setSavingReminder] = useState(false);

  const reload = async () => {
    const r = await apiFetch(`/api/surveys/${survey.id}`);
    const d = await r?.json();
    if (d?.id) setSurvey(d);
  };

  useEffect(() => {
    apiFetch('/api/surveys/reminder-settings').then(r => r?.json()).then(d => {
      if (d && !d.detail) setReminderSettings(d);
    });
  }, []);

  const toggleActive = async () => {
    const action = survey.is_active ? 'deactivate' : 'activate';
    await apiFetch(`/api/surveys/${survey.id}/${action}`, { method: 'POST' });
    reload();
  };

  const toggleInput = async () => {
    const action = survey.input_open ? 'close-input' : 'open-input';
    await apiFetch(`/api/surveys/${survey.id}/${action}`, { method: 'POST' });
    reload();
  };

  const sendInvitation = async () => {
    const count = await apiFetch(`/api/surveys/${survey.id}/send-invitation?dry_run=true`, { method: 'POST' }).then(r => r?.json());
    const total = count?.total || 0;
    if (!window.confirm(`Einladungs-E-Mail an ${total} Empfänger (Anbieter + Verwalter) senden?\n\nDies kann nicht rückgängig gemacht werden!`)) return;
    setSending('invitation');
    setSendResult(null);
    const r = await apiFetch(`/api/surveys/${survey.id}/send-invitation`, { method: 'POST' });
    const d = await r?.json();
    setSending(null);
    setSendResult(d?.message || 'Fehler');
  };

  const sendReminder = async () => {
    const count = await apiFetch(`/api/surveys/${survey.id}/send-reminder?dry_run=true`, { method: 'POST' }).then(r => r?.json());
    const total = count?.missing_total || 0;
    if (!window.confirm(`Erinnerungs-E-Mail an ${total} Anbieter ohne Meldung senden?\n\nDies kann nicht rückgängig gemacht werden!`)) return;
    setSending('reminder');
    setSendResult(null);
    const r = await apiFetch(`/api/surveys/${survey.id}/send-reminder`, { method: 'POST' });
    const d = await r?.json();
    setSending(null);
    setSendResult(d?.message || 'Fehler');
  };

  const saveReminderSettings = async () => {
    setSavingReminder(true);
    await apiFetch('/api/surveys/reminder-settings', {
      method: 'PUT',
      body: JSON.stringify(reminderSettings)
    });
    setSavingReminder(false);
  };

  return (
    <div data-testid="survey-detail">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={onBack} data-testid="survey-detail-back"><ArrowLeft size={14} /> Zurück zur Liste</button>
        <h2 style={{ margin: 0, fontSize: 20, flex: 1 }}>{survey.title}</h2>
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={() => onEdit(survey)} data-testid="survey-edit-btn"><Pencil size={14} /> Bearbeiten</button>
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={async () => {
          const r = await apiFetch(`/api/surveys/${survey.id}/export-excel`);
          if (r?.ok) { const b = await r.blob(); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = `Bestandserhebung_${survey.year}.xlsx`; a.click(); URL.revokeObjectURL(u); }
        }} data-testid="survey-export-excel"><Download size={14} /> Excel</button>
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={async () => {
          const r = await apiFetch(`/api/surveys/${survey.id}/export-pdf`);
          if (r?.ok) { const b = await r.blob(); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = `Bestandserhebung_Auswertung_${survey.year}.pdf`; a.click(); URL.revokeObjectURL(u); }
        }} data-testid="survey-export-pdf"><Download size={14} /> PDF</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="of-card">
          <div className="of-section"><Calendar size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Zeitraum</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Startdatum</div><div style={{ fontSize: 14, fontWeight: 500 }}>{formatDate(survey.start_date)}</div></div>
            <div><div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Enddatum</div><div style={{ fontSize: 14, fontWeight: 500 }}>{formatDate(survey.end_date)}</div></div>
          </div>
          <div style={{ marginTop: 12 }}><div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Jahr</div><div style={{ fontSize: 14, fontWeight: 500 }}>{survey.year}</div></div>
        </div>
        <div className="of-card">
          <div className="of-section">Status & Steuerung</div>
          <div style={{ marginBottom: 12 }}><div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Aktueller Status</div>{statusBadge(survey)}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className={`btn ${survey.is_active ? 'btn-secondary' : 'btn-primary'}`} style={{ padding: '6px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }} onClick={toggleActive} data-testid="survey-toggle-active">
              {survey.is_active ? <><XCircle size={14} /> Deaktivieren</> : <><CheckCircle size={14} /> Aktivieren</>}
            </button>
            {survey.is_active && <button className={`btn ${survey.input_open ? 'btn-secondary' : 'btn-primary'}`} style={{ padding: '6px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }} onClick={toggleInput} data-testid="survey-toggle-input">
              {survey.input_open ? <><Lock size={14} /> Eingabe sperren</> : <><Unlock size={14} /> Eingabe freigeben</>}
            </button>}
          </div>
        </div>
      </div>

      <ConfirmationTextsManager survey={survey} apiFetch={apiFetch} onUpdate={reload} />

      {/* E-Mail-Versand */}
      <div className="of-card" style={{ marginBottom: 16 }}>
        <div className="of-section"><Mail size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />E-Mail-Versand</div>

        {/* Test-Mail an sich selbst */}
        <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Test-Mail</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Empfänger:</label>
            <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
              className="of-input" style={{ flex: 1, maxWidth: 280, padding: '5px 10px', fontSize: 13 }}
              placeholder="ihre@email.de" data-testid="test-email-input" />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={async () => { setSending('test-inv'); setSendResult(null); const r = await apiFetch(`/api/surveys/${survey.id}/send-test-mail?template=invitation&email=${encodeURIComponent(testEmail)}`, { method: 'POST' }); const d = await r?.json(); setSending(null); setSendResult(d?.message || 'Fehler'); }}
              disabled={!!sending || !testEmail} data-testid="survey-test-invitation">
              <Mail size={12} /> {sending === 'test-inv' ? 'Wird gesendet...' : 'Test: Einladung'}
            </button>
            <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={async () => { setSending('test-rem'); setSendResult(null); const r = await apiFetch(`/api/surveys/${survey.id}/send-test-mail?template=reminder&email=${encodeURIComponent(testEmail)}`, { method: 'POST' }); const d = await r?.json(); setSending(null); setSendResult(d?.message || 'Fehler'); }}
              disabled={!!sending || !testEmail} data-testid="survey-test-reminder">
              <Clock size={12} /> {sending === 'test-rem' ? 'Wird gesendet...' : 'Test: Erinnerung'}
            </button>
          </div>
        </div>

        {/* Massen-Versand */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Versand an Empfänger</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" style={{ padding: '8px 18px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={sendInvitation} disabled={!!sending} data-testid="survey-send-invitation">
              <Mail size={14} /> {sending === 'invitation' ? 'Wird gesendet...' : 'Einladung versenden'}
            </button>
            <button className="btn btn-secondary" style={{ padding: '8px 18px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, borderColor: '#F59E0B', color: '#92400E' }}
              onClick={sendReminder} disabled={!!sending} data-testid="survey-send-reminder">
              <Clock size={14} /> {sending === 'reminder' ? 'Wird gesendet...' : 'Erinnerung versenden'}
            </button>
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 0' }}>
            <strong>Einladung:</strong> An alle Anbieter + Verwalter &nbsp;|&nbsp; <strong>Erinnerung:</strong> Nur an Anbieter ohne Meldung
          </p>
        </div>

        {sendResult && (
          <div style={{ padding: '8px 14px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: 13, marginBottom: 12 }} data-testid="send-result">
            {sendResult}
          </div>
        )}

        {/* Auto-Reminder Settings */}
        <div style={{ paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Automatische Erinnerung</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={reminderSettings.enabled}
                onChange={e => setReminderSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                data-testid="auto-reminder-enabled" />
              Aktiviert
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="number" min={1} max={30} value={reminderSettings.days_before_end}
                onChange={e => setReminderSettings(prev => ({ ...prev, days_before_end: parseInt(e.target.value) || 7 }))}
                className="of-input" style={{ width: 60, padding: '4px 8px', fontSize: 13 }}
                data-testid="auto-reminder-days" />
              Tage vor Meldefrist-Ende
            </label>
            <button className="btn btn-primary" style={{ padding: '4px 14px', fontSize: 12 }}
              onClick={saveReminderSettings} disabled={savingReminder} data-testid="save-reminder-settings">
              <Save size={12} /> Speichern
            </button>
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
            Sendet automatisch eine Erinnerung an alle Anbieter ohne Meldung, wenn die Meldefrist in weniger als {reminderSettings.days_before_end} Tagen endet.
          </p>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// SURVEY LIST (Bestandserhebungen tab)
// ═══════════════════════════════════════════════════════════════
function SurveyListTab({ surveys, onSelect, onNew, onDelete, apiFetch, onCopy }) {
  const [copyingId, setCopyingId] = useState(null);

  const handleCopy = async (e, s) => {
    e.stopPropagation();
    const nextYear = s.year + 1;
    if (!window.confirm(`Erhebung als Kopie für ${nextYear} erstellen?`)) return;
    setCopyingId(s.id);
    const r = await apiFetch(`/api/surveys/${s.id}/copy`, {
      method: 'POST',
      body: JSON.stringify({ year: nextYear })
    });
    const d = await r?.json();
    setCopyingId(null);
    if (d?.status) onCopy(d.survey);
    else alert(d?.detail || 'Fehler beim Kopieren');
  };

  return (
    <div data-testid="survey-list">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Angelegte Erhebungszeiträume</h3>
        <button className="btn btn-primary" onClick={onNew} data-testid="survey-new-btn" style={{ fontSize: 13 }}><Plus size={14} /> Neue Erhebung</button>
      </div>
      {surveys.length === 0 ? (
        <div className="of-card" style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>
          <Calendar size={32} style={{ marginBottom: 8, opacity: 0.4 }} /><p style={{ margin: 0, fontSize: 13 }}>Noch keine Erhebungen angelegt</p></div>
      ) : (
        <div className="of-card" style={{ padding: 0 }}>
          <div className="table-wrap" style={{ borderTop: 'none' }}>
            <table data-testid="surveys-table"><thead><tr>
              <th style={{ width: 60 }}>Jahr</th><th>Titel</th><th style={{ width: 200 }}>Zeitraum</th><th style={{ width: 80 }}>Bestimm.</th><th style={{ width: 200 }}>Status</th><th style={{ width: 120 }}>Aktionen</th>
            </tr></thead><tbody>
              {surveys.map(s => (
                <tr key={s.id} data-testid={`survey-row-${s.id}`} onClick={() => onSelect(s)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontWeight: 600, fontSize: 14 }}>{s.year}</td>
                  <td style={{ fontWeight: 500 }}>{s.title}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{formatDate(s.start_date)} – {formatDate(s.end_date)}</td>
                  <td style={{ textAlign: 'center' }}>{(s.confirmation_texts || []).length}</td>
                  <td>{statusBadge(s)}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn-icon edit" title="Details" onClick={() => onSelect(s)} data-testid={`survey-select-${s.id}`}><Pencil size={14} /></button>
                      <button className="btn-icon edit" title={`Kopie für ${s.year + 1} erstellen`} onClick={(e) => handleCopy(e, s)} disabled={copyingId === s.id} data-testid={`survey-copy-${s.id}`}><Copy size={14} /></button>
                      <button className="btn-icon" title="Löschen" onClick={() => onDelete(s.id)} style={{ color: '#EF4444' }} data-testid={`survey-delete-${s.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// PROVIDER SUBMISSION DETAIL (opened from tile click)
// ═══════════════════════════════════════════════════════════════
function ProviderSubmissionDetail({ item, survey, apiFetch, onBack, onRefresh }) {
  const [extending, setExtending] = useState(false);
  const [extendDate, setExtendDate] = useState('');
  const [showExtend, setShowExtend] = useState(false);
  const [editing, setEditing] = useState(false);
  const [offerTypes, setOfferTypes] = useState([]);
  const [groupCounts, setGroupCounts] = useState({});
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadOfferTypes = async () => {
    const r = await apiFetch(`/api/surveys/${survey.id}/offer-types/${item.provider_id}`);
    const d = await r?.json();
    if (Array.isArray(d)) {
      const names = d.map(t => typeof t === 'string' ? t : t.offer_type);
      const counts = {};
      d.forEach(t => {
        if (typeof t === 'string') { counts[t] = 0; }
        else { counts[t.offer_type] = t.group_count || 0; }
      });
      setOfferTypes(names);
      setGroupCounts(counts);
    }
  };

  const startEditing = async () => {
    await loadOfferTypes();
    if (item.data && item.data.length > 0) {
      setEntries(JSON.parse(JSON.stringify(item.data)));
    } else {
      setEntries([]);
    }
    setEditing(true);
  };

  // After offerTypes loaded, ensure we have entries for all types but keep existing data
  useEffect(() => {
    if (!editing || offerTypes.length === 0) return;
    setEntries(prev => {
      const typeSet = new Set(offerTypes);
      const result = [];
      const usedTypes = new Set();
      for (const e of prev) {
        if (typeSet.has(e.offer_type)) {
          result.push({ ...e, group_count: groupCounts[e.offer_type] || e.group_count });
          usedTypes.add(e.offer_type);
        }
      }
      for (const t of offerTypes) {
        if (!usedTypes.has(t)) {
          result.push({ offer_type: t, group_count: groupCounts[t] || 0, male_count: 0, female_count: 0, total_count: 0 });
        }
      }
      return result;
    });
  }, [offerTypes, editing]);

  const updateEntry = (idx, field, value) => {
    setEntries(prev => {
      const updated = [...prev];
      const numVal = Math.max(0, parseInt(value) || 0);
      updated[idx] = { ...updated[idx], [field]: numVal };
      if (field === 'male_count' || field === 'female_count') {
        const male = field === 'male_count' ? numVal : updated[idx].male_count;
        const female = field === 'female_count' ? numVal : updated[idx].female_count;
        updated[idx].total_count = male + female;
      }
      return updated;
    });
  };

  const handleSave = async (action = 'save') => {
    setSaving(true);
    setMessage(null);
    const r = await apiFetch(`/api/surveys/${survey.id}/submissions/${item.provider_id}`, {
      method: 'PUT',
      body: JSON.stringify({ data: entries, action })
    });
    const d = await r?.json();
    setSaving(false);
    if (d?.status) {
      setMessage({ type: 'success', text: d.message });
      setEditing(false);
      onRefresh();
    } else {
      setMessage({ type: 'error', text: d?.detail || 'Fehler' });
    }
  };

  const unfixate = async () => {
    if (!window.confirm('Fixierung wirklich aufheben? Das PDF wird gelöscht.')) return;
    await apiFetch(`/api/surveys/${survey.id}/submissions/${item.provider_id}/unfixate`, { method: 'POST' });
    onRefresh();
  };

  const regeneratePdf = async () => {
    if (!window.confirm('PDF neu generieren und in Dropbox hochladen?')) return;
    setSaving(true);
    setMessage(null);
    const r = await apiFetch(`/api/surveys/${survey.id}/submissions/${item.provider_id}/regenerate-pdf`, { method: 'POST' });
    const d = await r?.json();
    setSaving(false);
    if (d?.status) {
      setMessage({ type: 'success', text: d.message });
      onRefresh();
    } else {
      setMessage({ type: 'error', text: d?.detail || 'Fehler bei der PDF-Generierung' });
    }
  };

  const extendDeadline = async () => {
    if (!extendDate) return;
    setExtending(true);
    await apiFetch(`/api/surveys/${survey.id}/extend-deadline/${item.provider_id}`, {
      method: 'POST', body: JSON.stringify({ deadline: extendDate })
    });
    setShowExtend(false);
    setExtendDate('');
    setExtending(false);
    onRefresh();
  };

  const statusColor = { offen: '#dc2626', draft: '#f59e0b', submitted: '#2563eb', fixed: '#059669' };
  const statusText = { offen: 'Offen – keine Meldung', draft: 'Entwurf', submitted: 'Abgegeben', fixed: 'Fixiert' };
  const isFixed = item.status === 'fixed';

  return (
    <div data-testid="provider-submission-detail">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={onBack} data-testid="submission-detail-back"><ArrowLeft size={14} /> Zurück</button>
        <h3 style={{ margin: 0, fontSize: 18, flex: 1 }}>{item.provider_name}</h3>
        <span style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#fff', background: statusColor[item.status] || '#6b7280' }}>
          {statusText[item.status] || item.status}
        </span>
      </div>

      <div className="of-card" style={{ marginBottom: 16 }}>
        <div className="of-section">Anbieter-Info</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, fontSize: 13 }}>
          <div><span style={{ color: 'var(--text-muted)' }}>Club-ID:</span> <strong>{item.club_id || '-'}</strong></div>
          <div><span style={{ color: 'var(--text-muted)' }}>E-Mail:</span> <strong>{item.email || '-'}</strong></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Abgegeben am:</span> <strong>{item.submitted_at ? formatDate(item.submitted_at) : '-'}</strong></div>
        </div>
        {item.deadline_extension && <div style={{ marginTop: 8, fontSize: 13 }}><span style={{ color: '#b45309' }}>Frist verlängert bis:</span> <strong style={{ color: '#b45309' }}>{formatDate(item.deadline_extension)}</strong></div>}
        {item.admin_corrected_at && <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>Korrigiert durch Admin am {formatDate(item.admin_corrected_at)}</div>}
      </div>

      {/* Editing mode */}
      {editing ? (
        <div className="of-card" style={{ marginBottom: 16, padding: 0 }}>
          <div className="of-section" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>Meldung bearbeiten</div>
          <div className="table-wrap" style={{ borderTop: 'none' }}>
            <table data-testid="admin-edit-table"><thead><tr>
              <th>Angebot</th><th style={{ width: 90, textAlign: 'center' }}>Gruppen</th><th style={{ width: 90, textAlign: 'center' }}>Weiblich</th><th style={{ width: 90, textAlign: 'center' }}>Männlich</th><th style={{ width: 80, textAlign: 'center' }}>Gesamt</th>
            </tr></thead><tbody>
              {entries.map((e, idx) => (
                <tr key={e.offer_type} data-testid={`admin-entry-${idx}`}>
                  <td style={{ fontWeight: 500, fontSize: 13 }}>{e.offer_type}</td>
                  <td><div style={{ textAlign: 'center', padding: '4px 8px', fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-light, #f1f5f9)', borderRadius: 4 }} data-testid={`admin-groups-${idx}`}>{e.group_count || 0}</div></td>
                  <td><input className="of-input" type="number" min="0" value={e.female_count || ''} onChange={ev => updateEntry(idx, 'female_count', ev.target.value)} style={{ textAlign: 'center', padding: '4px 8px' }} data-testid={`admin-female-${idx}`} /></td>
                  <td><input className="of-input" type="number" min="0" value={e.male_count || ''} onChange={ev => updateEntry(idx, 'male_count', ev.target.value)} style={{ textAlign: 'center', padding: '4px 8px' }} data-testid={`admin-male-${idx}`} /></td>
                  <td style={{ textAlign: 'center', fontWeight: 600 }}>{e.total_count || 0}</td>
                </tr>
              ))}
              <tr style={{ background: 'var(--bg-light, #f8fafc)', fontWeight: 600 }}>
                <td>Gesamt</td>
                <td style={{ textAlign: 'center' }}>{entries.reduce((s, e) => s + (e.group_count || 0), 0)}</td>
                <td style={{ textAlign: 'center' }}>{entries.reduce((s, e) => s + (e.female_count || 0), 0)}</td>
                <td style={{ textAlign: 'center' }}>{entries.reduce((s, e) => s + (e.male_count || 0), 0)}</td>
                <td style={{ textAlign: 'center' }}>{entries.reduce((s, e) => s + (e.total_count || 0), 0)}</td>
              </tr>
            </tbody></table>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" style={{ padding: '6px 16px', fontSize: 13 }} onClick={() => handleSave('submit')} disabled={saving} data-testid="admin-save-submit"><Save size={14} /> Speichern & als abgegeben markieren</button>
            <button className="btn btn-secondary" style={{ padding: '6px 16px', fontSize: 13 }} onClick={() => handleSave('save')} disabled={saving} data-testid="admin-save-draft"><Save size={14} /> Nur speichern</button>
            <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={() => setEditing(false)}>Abbrechen</button>
          </div>
        </div>
      ) : (
        <>
          {/* Read-only data table */}
          {item.data && item.data.length > 0 ? (
            <div className="of-card" style={{ marginBottom: 16, padding: 0 }}>
              <div className="of-section" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>Gemeldete Teilnehmerzahlen</div>
              <div className="table-wrap" style={{ borderTop: 'none' }}>
                <table data-testid="submission-data-table"><thead><tr>
                  <th>Angebot</th><th style={{ width: 80, textAlign: 'center' }}>Gruppen</th><th style={{ width: 80, textAlign: 'center' }}>Weiblich</th><th style={{ width: 80, textAlign: 'center' }}>Männlich</th><th style={{ width: 80, textAlign: 'center' }}>Gesamt</th>
                </tr></thead><tbody>
                  {item.data.map((e, i) => (
                    <tr key={i}><td>{e.offer_type}</td><td style={{ textAlign: 'center' }}>{e.group_count || 0}</td><td style={{ textAlign: 'center' }}>{e.female_count || 0}</td><td style={{ textAlign: 'center' }}>{e.male_count || 0}</td><td style={{ textAlign: 'center', fontWeight: 600 }}>{e.total_count || 0}</td></tr>
                  ))}
                  <tr style={{ background: 'var(--bg-light, #f8fafc)', fontWeight: 600 }}>
                    <td>Gesamt</td>
                    <td style={{ textAlign: 'center' }}>{item.total_groups}</td>
                    <td style={{ textAlign: 'center' }}>{item.data.reduce((s, e) => s + (e.female_count || 0), 0)}</td>
                    <td style={{ textAlign: 'center' }}>{item.data.reduce((s, e) => s + (e.male_count || 0), 0)}</td>
                    <td style={{ textAlign: 'center' }}>{item.total_participants}</td>
                  </tr>
                </tbody></table>
              </div>
            </div>
          ) : (
            <div className="of-card" style={{ marginBottom: 16, textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: 13 }}>Keine Meldungsdaten vorhanden.</div>
          )}
        </>
      )}

      {/* Message */}
      {message && (
        <div style={{ marginBottom: 12, padding: '10px 16px', borderRadius: 6, fontSize: 13,
          background: message.type === 'success' ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${message.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
          color: message.type === 'success' ? '#065f46' : '#991b1b',
        }} data-testid="admin-message">{message.text}</div>
      )}

      {/* Actions */}
      {!editing && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isFixed && (
            <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={startEditing} data-testid="admin-edit-submission-btn">
              <Pencil size={14} /> Meldung bearbeiten / erstellen
            </button>
          )}
          {isFixed && (
            <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={unfixate} data-testid="unfixate-btn">
              <Unlock size={14} /> Fixierung aufheben
            </button>
          )}
          {isFixed && (
            <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={regeneratePdf} disabled={saving} data-testid="regenerate-pdf-btn">
              <RefreshCw size={14} /> PDF neu generieren
            </button>
          )}
          <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={() => setShowExtend(!showExtend)} data-testid="extend-deadline-toggle">
            <CalendarPlus size={14} /> Frist verlängern
          </button>
        </div>
      )}

      {showExtend && (
        <div style={{ marginTop: 12, padding: 12, background: 'var(--bg-light, #f8fafc)', border: '1px solid var(--border)', borderRadius: 6 }} data-testid="extend-deadline-form">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="date" className="of-input" value={extendDate} onChange={e => setExtendDate(e.target.value)} style={{ width: 200 }} data-testid="extend-deadline-date" />
            <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: 12 }} onClick={extendDeadline} disabled={extending || !extendDate} data-testid="extend-deadline-save"><Save size={12} /> Speichern</button>
            <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 12 }} onClick={() => setShowExtend(false)}>Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// MELDUNGEN TAB (with year tiles + provider cards + filter)
// ═══════════════════════════════════════════════════════════════
function MeldungenTab({ surveys, apiFetch }) {
  const [selectedYear, setSelectedYear] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('alle');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Distinct years from surveys, sorted descending
  const years = [...new Set(surveys.map(s => s.year))].sort((a, b) => b - a);

  // Auto-select most recent year
  useEffect(() => {
    if (years.length > 0 && !selectedYear) setSelectedYear(years[0]);
  }, [years]);

  useEffect(() => {
    if (!selectedYear) return;
    loadSubmissions();
  }, [selectedYear]);

  const loadSubmissions = async () => {
    const sv = surveys.find(s => s.year === selectedYear);
    if (!sv) return;
    setSurvey(sv);
    setLoading(true);
    const r = await apiFetch(`/api/surveys/${sv.id}/submissions`);
    const d = await r?.json();
    if (d?.submissions) setSubmissions(d.submissions);
    setLoading(false);
  };

  const filtered = submissions.filter(s => {
    if (filter === 'offen' && s.status !== 'offen') return false;
    if (filter === 'gemeldet' && s.status === 'offen') return false;
    if (filter === 'fixiert' && s.status !== 'fixed') return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (!s.provider_name.toLowerCase().includes(term) && !(s.club_id || '').toLowerCase().includes(term)) return false;
    }
    return true;
  });

  const counts = {
    alle: submissions.length,
    offen: submissions.filter(s => s.status === 'offen').length,
    gemeldet: submissions.filter(s => s.status !== 'offen').length,
    fixiert: submissions.filter(s => s.status === 'fixed').length,
  };

  const statusColor = { offen: '#dc2626', draft: '#f59e0b', submitted: '#2563eb', fixed: '#059669' };
  const statusText = { offen: 'Offen', draft: 'Entwurf', submitted: 'Abgegeben', fixed: 'Fixiert' };

  // Provider detail
  if (selectedItem && survey) {
    return <ProviderSubmissionDetail item={selectedItem} survey={survey} apiFetch={apiFetch}
      onBack={() => setSelectedItem(null)} onRefresh={() => { loadSubmissions(); setSelectedItem(null); }} />;
  }

  return (
    <div data-testid="meldungen-tab">
      {/* Year tiles */}
      {years.length === 0 ? (
        <div className="of-card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: 13 }}>
          Keine Erhebungszeiträume vorhanden. Bitte legen Sie zuerst eine Erhebung an.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }} data-testid="year-tiles">
            {years.map(y => (
              <button key={y} onClick={() => { setSelectedYear(y); setSelectedItem(null); }}
                className={selectedYear === y ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{ padding: '8px 20px', fontSize: 14, fontWeight: 600, minWidth: 80 }}
                data-testid={`year-tile-${y}`}>
                {y}
              </button>
            ))}
            {survey && (
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 12 }} data-testid="meldungen-export-excel"
                  onClick={async () => {
                    const statusParam = filter !== 'alle' ? `?status=${filter}` : '';
                    const r = await apiFetch(`/api/surveys/${survey.id}/export-excel${statusParam}`);
                    if (r?.ok) { const b = await r.blob(); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = `Bestandserhebung_${survey.year}.xlsx`; a.click(); URL.revokeObjectURL(u); }
                  }}>
                  <Download size={14} /> Excel-Export
                </button>
                <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 12 }} data-testid="meldungen-export-pdf"
                  onClick={async () => {
                    const statusParam = filter !== 'alle' ? `?status=${filter}` : '';
                    const r = await apiFetch(`/api/surveys/${survey.id}/export-pdf${statusParam}`);
                    if (r?.ok) { const b = await r.blob(); const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = `Bestandserhebung_Auswertung_${survey.year}.pdf`; a.click(); URL.revokeObjectURL(u); }
                  }}>
                  <Download size={14} /> PDF-Export
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>
          ) : (
            <>
              {/* Filter bar */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }} data-testid="filter-bar">
                {[
                  { key: 'alle', label: 'Alle' },
                  { key: 'offen', label: 'Offen' },
                  { key: 'gemeldet', label: 'Gemeldet' },
                  { key: 'fixiert', label: 'Fixiert' },
                ].map(f => (
                  <button key={f.key} onClick={() => setFilter(f.key)}
                    style={{
                      padding: '5px 14px', fontSize: 12, borderRadius: 20, border: '1px solid var(--border)',
                      background: filter === f.key ? 'var(--primary)' : 'var(--bg-card, #fff)',
                      color: filter === f.key ? '#fff' : 'var(--text)',
                      cursor: 'pointer', fontWeight: filter === f.key ? 600 : 400,
                    }}
                    data-testid={`filter-${f.key}`}>
                    {f.label} ({counts[f.key]})
                  </button>
                ))}
                <div style={{ flex: 1 }} />
                <div style={{ position: 'relative' }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input className="of-input" placeholder="Suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    style={{ paddingLeft: 30, width: 200, fontSize: 12 }} data-testid="search-input" />
                </div>
              </div>

              {/* Provider tiles */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }} data-testid="provider-tiles">
                {filtered.map(s => (
                  <div key={s.provider_id} className="of-card" onClick={() => setSelectedItem(s)}
                    style={{ cursor: 'pointer', padding: '14px 16px', transition: 'box-shadow 0.15s', borderLeft: `4px solid ${statusColor[s.status] || '#6b7280'}` }}
                    data-testid={`provider-tile-${s.provider_id}`}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.1)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = ''}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <div style={{ flex: 1, fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>{s.provider_name}</div>
                      <ChevronRight size={14} color="var(--text-muted)" />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                        background: `${statusColor[s.status]}15`, color: statusColor[s.status]
                      }}>
                        {statusText[s.status] || s.status}
                      </span>
                      {s.club_id && <span style={{ color: 'var(--text-muted)' }}>{s.club_id}</span>}
                      {s.total_participants > 0 && <span style={{ color: 'var(--text-muted)' }}>| {s.total_participants} TN</span>}
                      {s.deadline_extension && <span style={{ color: '#b45309' }}>Frist: {formatDate(s.deadline_extension)}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {filtered.length === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: 13 }}>Keine Anbieter für diesen Filter gefunden.</div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function SurveysPage() {
  const { apiFetch } = useApi();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('meldungen'); // bestandserhebungen | meldungen
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);

  const loadSurveys = async () => {
    setLoading(true);
    const r = await apiFetch('/api/surveys');
    const d = await r?.json();
    if (Array.isArray(d)) setSurveys(d);
    setLoading(false);
  };

  useEffect(() => { loadSurveys(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Erhebung wirklich löschen?')) return;
    const r = await apiFetch(`/api/surveys/${id}`, { method: 'DELETE' });
    const d = await r?.json();
    if (d?.status) loadSurveys();
    else alert(d?.detail || 'Fehler beim Löschen');
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>;

  // Detail view (from Bestandserhebungen tab)
  if (view === 'detail' && selected) {
    return <SurveyDetail survey={selected} apiFetch={apiFetch}
      onBack={() => { setView('list'); setSelected(null); loadSurveys(); }}
      onEdit={s => { setSelected(s); setView('edit'); }} />;
  }
  if (view === 'edit') {
    return <SurveyEditor survey={selected} apiFetch={apiFetch}
      onSave={s => { if (s) { setSelected(s); setView('detail'); } else { setView('list'); setSelected(null); } loadSurveys(); }}
      onCancel={() => { if (selected?.id) setView('detail'); else { setView('list'); setSelected(null); } }} />;
  }

  return (
    <div data-testid="surveys-page">
      <h2 style={{ margin: '0 0 16px', fontSize: 20 }}>Bestandserhebung</h2>

      {/* Tab navigation */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid var(--border)' }} data-testid="surveys-tabs">
        {[
          { key: 'meldungen', label: 'Meldungen', icon: <Users size={15} /> },
          { key: 'bestandserhebungen', label: 'Bestandserhebungen', icon: <Calendar size={15} /> },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            style={{
              padding: '10px 20px', fontSize: 13, fontWeight: tab === t.key ? 600 : 400,
              border: 'none', borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
              background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)', marginBottom: -2,
            }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === 'bestandserhebungen' && (
        <SurveyListTab surveys={surveys} apiFetch={apiFetch}
          onSelect={s => { setSelected(s); setView('detail'); }}
          onNew={() => { setSelected(null); setView('edit'); }}
          onDelete={handleDelete}
          onCopy={() => loadSurveys()} />
      )}

      {tab === 'meldungen' && (
        <MeldungenTab surveys={surveys} apiFetch={apiFetch} />
      )}
    </div>
  );
}
